import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { AnalyticsService } from 'app/shared/analytics/services/analytics.service';
import { MembershipActions } from './membership.actions';
import {
  BulkMembershipCategoryOperationResult,
  formatMembershipLevelApiError,
  MembershipLevelService,
} from '../services/membership-level.service';

@Injectable()
export class MembershipEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly membershipApi: MembershipLevelService,
    private readonly toastr: ToastrService,
    private readonly analytics: AnalyticsService,
  ) {}

  loadLevels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipActions.loadLevels),
      switchMap(({ propertyId }) =>
        this.membershipApi.getAll(propertyId).pipe(
          map(levels => MembershipActions.loadLevelsSuccess({ propertyId, levels })),
          catchError(error => {
            const msg = formatMembershipLevelApiError(error, 'Could not load membership levels.');
            this.toastr.error(msg, 'Error');
            return of(MembershipActions.loadLevelsFailure({ error }));
          }),
        ),
      ),
    ),
  );

  createLevel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipActions.createLevel),
      mergeMap(({ propertyId, draft }) =>
        this.membershipApi.create(propertyId, draft).pipe(
          tap(() => this.analytics.membershipLevelCreatedEvent('Manual')),
          map(level => MembershipActions.createLevelSuccess({ propertyId, level })),
          catchError(error => {
            const msg = formatMembershipLevelApiError(error, 'Could not save membership level.');
            this.toastr.error(msg, 'Error');
            return of(MembershipActions.createLevelFailure({ error }));
          }),
        ),
      ),
    ),
  );

  updateLevel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipActions.updateLevel),
      mergeMap(({ propertyId, level }) =>
        this.membershipApi.update(propertyId, level).pipe(
          map(saved => MembershipActions.updateLevelSuccess({ propertyId, level: saved })),
          catchError(error => {
            const msg = formatMembershipLevelApiError(error, 'Could not save membership level.');
            this.toastr.error(msg, 'Error');
            return of(MembershipActions.updateLevelFailure({ error }));
          }),
        ),
      ),
    ),
  );

  deleteLevel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipActions.deleteLevel),
      mergeMap(({ propertyId, membershipCategoryId }) =>
        this.membershipApi.delete(propertyId, membershipCategoryId).pipe(
          mergeMap(res => {
            if (res.blockedByActiveMembers) {
              return of(MembershipActions.deleteLevelBlocked());
            }
            if (res.ok) {
              return of(MembershipActions.deleteLevelSuccess({ propertyId, membershipCategoryId }));
            }
            return of(MembershipActions.deleteLevelFailure({ error: 'Delete not acknowledged' }));
          }),
          catchError(error => of(MembershipActions.deleteLevelFailure({ error }))),
        ),
      ),
    ),
  );

  bulkUpdateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipActions.bulkUpdateStatus),
      mergeMap(({ propertyId, membershipCategoryIds, status }) =>
        this.membershipApi.bulkUpdateStatus(propertyId, membershipCategoryIds, status).pipe(
          tap(res => this.toastBulkResult(res, 'status')),
          map(() => MembershipActions.loadLevels({ propertyId })),
          catchError(error => {
            const msg = formatMembershipLevelApiError(error, 'Could not update status.');
            this.toastr.error(msg, 'Error');
            return of(
              MembershipActions.bulkUpdateStatusFailure({ error }),
              MembershipActions.loadLevels({ propertyId }),
            );
          }),
        ),
      ),
    ),
  );

  bulkDeleteLevels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MembershipActions.bulkDeleteLevels),
      mergeMap(({ propertyId, membershipCategoryIds }) =>
        this.membershipApi.bulkDelete(propertyId, membershipCategoryIds).pipe(
          tap(res => this.toastBulkResult(res, 'delete')),
          map(() => MembershipActions.loadLevels({ propertyId })),
          catchError(error => {
            const msg = formatMembershipLevelApiError(error, 'Could not remove membership levels.');
            this.toastr.error(msg, 'Error');
            return of(
              MembershipActions.bulkDeleteLevelsFailure({ error }),
              MembershipActions.loadLevels({ propertyId }),
            );
          }),
        ),
      ),
    ),
  );

  private toastBulkResult(res: BulkMembershipCategoryOperationResult, kind: 'status' | 'delete'): void {
    const n = res.successCount;
    const errCount = res.errors.length;
    const past = kind === 'status' ? 'updated' : 'removed';
    const failPhrase = kind === 'status' ? 'could not be updated' : 'could not be removed';
    if (errCount === 0) {
      const msg = n === 1 ? `1 membership level ${past}.` : `${n} membership levels ${past}.`;
      this.toastr.success(msg, 'Success');
      return;
    }
    if (n > 0) {
      this.toastr.warning(`${n} level(s) ${past}; ${errCount} ${failPhrase}.`, 'Partial success');
      return;
    }
    this.toastr.error('No levels were processed.', 'Error');
  }
}
